import React from 'react';
import { LuCalendar, LuClock } from 'react-icons/lu';
import { formatDuration, formatPrice } from '../../../utils/pricing';
import { formatMinutesAsTime, parseTimeToMinutes } from '../../../utils/time';
import { normalizeBookingServices } from '../../../utils/serviceBuilder';
import styles from './checkoutSummary.module.css';

function CheckoutSummary({
	booking = {},
	endTime = '',
	price = 0,
	discount = null,
	discountAmount = 0,
	duration = 0,
	deposit = null,
	agreed = false,
	onAgreeChange = () => {},
	formId,
	disabled = false,
	processing = false,
}) {
	const services = normalizeBookingServices(booking);
	const hasDeposit = Number.isFinite(deposit) && deposit > 0;
	const remaining = hasDeposit ? Math.max(0, price - deposit) : price;
	const depositLabel = hasDeposit ? `a ${formatPrice(deposit)}` : 'a';
	const formattedDate = booking.date
		? new Date(`${booking.date}T00:00:00`).toLocaleDateString('en-US', {
			weekday: 'long',
			month: 'long',
			day: 'numeric',
			year: 'numeric',
		})
		: '';
	const startTime = booking.slotValue ? formatMinutesAsTime(parseTimeToMinutes(booking.slotValue)) : '';

	return (
		<div className={styles.summary}>
			<h1 className={styles.summaryTitle}>Appointment Summary</h1>

			<div className={styles.card}>
				<div className={styles.detailRow}>
					<LuCalendar aria-hidden="true" className={styles.detailIcon} />
					<span>{formattedDate}</span>
				</div>
				<div className={styles.detailRow}>
					<LuClock aria-hidden="true" className={styles.detailIcon} />
					<span>{startTime} – {endTime}</span>
				</div>
			</div>

			{services.map((service) => (
				<div key={service.product_id} className={styles.card}>
					<div className={styles.serviceHeader}>
						<h3 className={styles.serviceName}>{service.product_name}</h3>
						<span className={styles.servicePrice}>{formatPrice(service.total_price)}</span>
					</div>
					{service.attributes.length > 0 && (
						<ul className={styles.attributeList}>
							{service.attributes.map(({ name, value }) => (
								<li key={`${service.product_id}-${name}`} className={styles.serviceAttribute}>
									<span className={styles.attributeName}>{name}</span>
									<span className={styles.attributeValue}>{value}</span>
								</li>
							))}
						</ul>
					)}
					<div className={styles.serviceFooter}>
						<span className={styles.serviceDuration}>
							<LuClock aria-hidden="true" />
							{formatDuration(service.total_duration)}
						</span>
					</div>
				</div>
			))}

			<div className={styles.footer}>
				<div className={styles.totals}>
					<dl className={styles.breakdown}>
						{discount && (
							<>
								<div className={styles.breakdownRow}>
									<dt>Service subtotal</dt>
									<dd>{formatPrice(price + discountAmount)}</dd>
								</div>
								<div className={styles.breakdownRow}>
									<dt>{discount.name} ({discount.percentage}% off)</dt>
									<dd>-{formatPrice(discountAmount)}</dd>
								</div>
							</>
						)}
						<div className={styles.breakdownRow}>
							<dt>Due today</dt>
							<dd className={styles.dueToday}>{formatPrice(0)}</dd>
						</div>
						{deposit !== null && (
							<div className={styles.breakdownRow}>
								<dt>{hasDeposit ? 'Deposit when confirmed' : 'No deposit required'}</dt>
								<dd>{formatPrice(deposit)}</dd>
							</div>
						)}
						<div className={styles.breakdownRow}>
							<dt>Remaining after appointment</dt>
							<dd>{formatPrice(remaining)}</dd>
						</div>
					</dl>
					<div className={styles.breakdownTotal}>
						<div className={styles.totalsLabel}>
							<span>Appointment total</span>
							<span className={styles.totalsMeta}>{formatDuration(duration)}</span>
						</div>
						<span className={styles.totalsPrice}>{formatPrice(price)}</span>
					</div>
				</div>

				<label className={styles.consent}>
					<input
						type="checkbox"
						checked={agreed}
						onChange={(e) => onAgreeChange(e.target.checked)}
					/>
					<span className={styles.consentText}>
						{hasDeposit
							? `I authorize Nails By Astraea to charge ${depositLabel} deposit to my card when my appointment is confirmed, and the remaining balance after my appointment. `
							: 'No deposit is required. I authorize Nails By Astraea to charge the full balance to my card after my appointment. '}
						Charges for no-shows or late cancellations follow the payment policies.{' '}
						<a href="/policy#payment" target="_blank" rel="noopener noreferrer">
							View Payment Policies.
						</a>
					</span>
				</label>

				<button type="submit" form={formId} disabled={disabled || !agreed} className={styles.submitButton}>
					{processing ? 'Processing…' : 'Request Appointment'}
				</button>
				<p className={styles.submitHint}>You'll receive a text or email once your appointment is confirmed. <span> <a href="/policy#privacy" target="_blank" rel="noopener noreferrer">View Privacy Policy.</a></span></p>
			</div>
		</div>
	);
}

export default CheckoutSummary;
